/**
 * Hook for handling date range filter changes with mutual exclusivity.
 *
 * Configuration-driven: Uses DATE_RANGE_CONFIGS from filterConfig.ts
 * to support different date range filters (due_date, created_at, last_update).
 *
 * Encapsulates the logic that:
 * - Setting a date range clears the "no date" flag (if configured)
 * - Setting "no date" clears the date range
 *
 * This keeps components from knowing about these business rules.
 *
 * WHEN TO USE THIS HOOK:
 * - Panel-style filters with batch state (e.g., AdvancedFiltersPanel)
 * - When you have a filters object and setFilters function
 *
 * WHEN NOT TO USE:
 * - DateRangeColumnFilter uses immediate callbacks (onChange, onDateRangeFilterChange)
 * - Different integration pattern - has its own inline mutual exclusivity logic
 * - See DateRangeColumnFilter.tsx for that implementation
 */

import { useCallback } from 'react';

import { FilterRecord } from '@/services/types/pagination-types';

import { DATE_RANGE_CONFIGS, DateRangeConfigKey } from '../config/filterConfig';

interface UseDateRangeFilterOptions {
  /** Current filter state */
  filters: FilterRecord;
  /** Function to update filters */
  setFilters: (filters: FilterRecord) => void;
  /**
   * Which date range configuration to use.
   * References DATE_RANGE_CONFIGS in filterConfig.ts.
   * @example 'due_date' | 'created_at' | 'last_update'
   */
  configKey: DateRangeConfigKey;
}

interface UseDateRangeFilterReturn {
  /** Handle date range changes (clears "no date" flag if configured) */
  handleDateRangeChange: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  /**
   * Handle "no date" toggle (clears date range).
   * Only available if the config has noDueDateKey.
   */
  handleNoDueDateChange: ((value: boolean) => void) | undefined;
  /** Current start date value */
  startDate: string | undefined;
  /** Current end date value */
  endDate: string | undefined;
  /** Current "no date" value (false if not configured) */
  noDueDate: boolean;
  /** Whether this date range supports "no date" option */
  hasNoDueDateOption: boolean;
}

/**
 * Hook for managing date range filters with automatic mutual exclusivity.
 *
 * @example
 * ```tsx
 * // For due date filter (with "no due date" option)
 * const { handleDateRangeChange, handleNoDueDateChange, startDate, endDate, noDueDate } =
 *   useDateRangeFilter({
 *     filters: state.filters,
 *     setFilters: actions.setFilters,
 *     configKey: 'due_date',
 *   });
 *
 * // For created_at filter (no "no date" option)
 * const { handleDateRangeChange, startDate, endDate } =
 *   useDateRangeFilter({
 *     filters: matterFilters,
 *     setFilters: setMatterFilters,
 *     configKey: 'created_at',
 *   });
 * ```
 */
export function useDateRangeFilter({
  filters,
  setFilters,
  configKey,
}: UseDateRangeFilterOptions): UseDateRangeFilterReturn {
  // Get configuration for this date range type
  const config = DATE_RANGE_CONFIGS[configKey];
  const { startKey, endKey } = config;
  const noDueDateKey =
    'noDueDateKey' in config ? config.noDueDateKey : undefined;

  // Extract current values using configured keys
  const startDate = filters[startKey] as string | undefined;
  const endDate = filters[endKey] as string | undefined;

  const noDueDateRaw = noDueDateKey ? filters[noDueDateKey] : undefined;
  const noDueDate = noDueDateRaw === 'true' || String(noDueDateRaw) === 'true';

  // Handle date range changes - clears "no date" flag (mutual exclusivity)
  const handleDateRangeChange = useCallback(
    (newStartDate: string | undefined, newEndDate: string | undefined) => {
      const newFilters = { ...filters };

      // Update or remove start date
      if (newStartDate) {
        newFilters[startKey] = newStartDate;
      } else {
        delete newFilters[startKey];
      }

      // Update or remove end date
      if (newEndDate) {
        newFilters[endKey] = newEndDate;
      } else {
        delete newFilters[endKey];
      }

      // Mutual exclusivity: clear "no date" flag when setting any date
      if (noDueDateKey) {
        delete newFilters[noDueDateKey];
      }

      setFilters(newFilters);
    },
    [filters, setFilters, startKey, endKey, noDueDateKey],
  );

  // Handle "no date" toggle - clears date range (mutual exclusivity)
  // Returns undefined if config doesn't have noDueDateKey
  const handleNoDueDateChangeInternal = useCallback(
    (value: boolean) => {
      if (!noDueDateKey) return; // No-op if not configured

      const newFilters = { ...filters };

      if (value) {
        // Setting "no date": clear date range
        newFilters[noDueDateKey] = 'true';
        delete newFilters[startKey];
        delete newFilters[endKey];
      } else {
        // Clearing "no date"
        delete newFilters[noDueDateKey];
      }

      setFilters(newFilters);
    },
    [filters, setFilters, startKey, endKey, noDueDateKey],
  );

  // Only expose the handler if this config supports "no date" option
  const handleNoDueDateChange = noDueDateKey
    ? handleNoDueDateChangeInternal
    : undefined;

  return {
    handleDateRangeChange,
    handleNoDueDateChange,
    startDate,
    endDate,
    noDueDate,
    hasNoDueDateOption: Boolean(noDueDateKey),
  };
}
